import React, { useRef, useState } from "react";

import {
  FakeLink,
  InputLabel,
  NoWrapFlashPCBTable,
  TableHeaderCell,
  TableRow,
} from "../../../../constants/Styles";
import {
  Container,
  InventoryResponse,
} from "../../../../reducers/componentSearch";
import {
  CellRow,
  ErrorText,
  InventoryHeaderRow,
  InventoryTableCell,
  TableContainer,
} from "./InventoryStyles";
import InfoItem from "../PartDetails/InfoItem";
import _ from "lodash";
import Link from "next/link";
import {
  dateStringToHumanReadableDate,
  datetimeStringToHumanReadableDatetime,
  formatSmallCurrency,
  twentyFourHourToTwelveHour,
} from "../../../../utils/valueFormatting";
import { useFlashPCBSettings } from "../../../../utils/hooks/commonly-stored/flashPCBSettings";
import LoadingIcon from "../../../common/LoadingIcon";
import LoadingOverlay from "../../../LoadingOverlay";
import { useUpdateInventoryOverride } from "./OverrideVirtualInventory";
import AllocationsDetails from "../../../internal/parts/AllocationsDetails";
import { BinLink, ContainerLink } from "../../../internal/parts/BarcodeLink";
import ConsignmentUserInfo from "./PhysicalInventory/ConsignmentUserInfo";
import HorizontalScroll from "../../../common/HorizontalScroll";
import DeleteContainer from "./PhysicalInventory/DeleteContainer";
import Modal from "../../../common/Modal";
import ModalContentWithCloseButton from "../../../common/ModalContentWithCloseButton";
import { PartDrawerInventorySubTitle } from "../../../internal/parts/InventoryUpdates/InventoryUpdatesStyles";
import ApproveVirtualInventory from "./ApproveVirtualInventory";

export default function InventoryDetailsTable({
  inventoryResponse,
  onSelectContainer,
  partId,
  onFinishedEditing,
}: {
  inventoryResponse: InventoryResponse;
  partId: string;
  onFinishedEditing: () => void;
  onSelectContainer: (container: Container) => void;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const flashPCBSettings = useFlashPCBSettings();
  const [containerToDelete, setContainerToDelete] = useState<string | null>(
    null,
  );
  const { loading, errorMessage, updateInventoryOverride } =
    useUpdateInventoryOverride(onFinishedEditing, partId);

  const closeModal = () => {
    setContainerToDelete(null);
  };

  if (flashPCBSettings === null) {
    return <LoadingIcon />;
  }

  if (
    inventoryResponse.virtual_inventory.sellers_inventory.length > 0 ||
    inventoryResponse.physical_inventory.containers.length > 0 ||
    inventoryResponse.virtual_inventory.override !== null
  ) {
    return (
      <>
        <TableContainer>
          <HorizontalScroll childrenRef={tableRef}>
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
            <LoadingOverlay loading={loading} text={"Saving override"} />
            <NoWrapFlashPCBTable ref={tableRef}>
              <thead>
                <InventoryHeaderRow>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell alignRight>
                    Last counted amount
                  </TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                  <TableHeaderCell>Details</TableHeaderCell>
                </InventoryHeaderRow>
              </thead>
              <tbody>
                {inventoryResponse.virtual_inventory.override !== null && (
                  <TableRow>
                    <InventoryTableCell>Override</InventoryTableCell>
                    <InventoryTableCell>
                      <InfoItem
                        label={"Expires"}
                        value={`${twentyFourHourToTwelveHour(flashPCBSettings.local_lead_time_eod)} on ${dateStringToHumanReadableDate(inventoryResponse.virtual_inventory.override.expiration, true)}`}
                      />
                    </InventoryTableCell>
                    <InventoryTableCell alignRight>
                      {inventoryResponse.virtual_inventory.override.inventory_level.toLocaleString()}
                    </InventoryTableCell>
                    <InventoryTableCell>
                      <FakeLink onClick={() => updateInventoryOverride(null)}>
                        Delete override
                      </FakeLink>
                    </InventoryTableCell>
                    <InventoryTableCell>
                      <CellRow>
                        <InfoItem
                          label={"Minimum order quantity"}
                          value={inventoryResponse.virtual_inventory.override.minimum_order_quantity.toLocaleString()}
                        />
                        <InfoItem
                          label={"Notes"}
                          value={
                            inventoryResponse.virtual_inventory.override.notes
                          }
                        />
                      </CellRow>
                      <CellRow>
                        {inventoryResponse.virtual_inventory.override.price_breaks.map(
                          (priceBreak) => {
                            return (
                              <InfoItem
                                key={priceBreak.quantity}
                                label={priceBreak.quantity.toLocaleString()}
                                value={formatSmallCurrency(
                                  priceBreak.unit_price,
                                  priceBreak.currency,
                                )}
                              />
                            );
                          },
                        )}
                      </CellRow>
                    </InventoryTableCell>
                  </TableRow>
                )}
                {_.orderBy(
                  inventoryResponse.virtual_inventory.sellers_inventory,
                  "inventory_level",
                  "desc",
                ).flatMap((sellerInventory) => {
                  const numOfferings = sellerInventory.offerings.length;
                  return sellerInventory.offerings.map(
                    (offering, offeringIndex) => {
                      return (
                        <TableRow key={offering.distributor_link}>
                          {offeringIndex == 0 && (
                            <>
                              <InventoryTableCell rowSpan={numOfferings}>
                                Virtual
                              </InventoryTableCell>
                              <InventoryTableCell rowSpan={numOfferings}>
                                {sellerInventory.seller}
                              </InventoryTableCell>
                              <InventoryTableCell
                                rowSpan={numOfferings}
                                alignRight
                              >
                                {sellerInventory.inventory_level.toLocaleString()}
                              </InventoryTableCell>
                            </>
                          )}
                          <InventoryTableCell>
                            <Link
                              href={offering.distributor_link}
                              target={"_blank"}
                            >
                              See offer
                            </Link>
                          </InventoryTableCell>
                          <InventoryTableCell>
                            <CellRow>
                              {offering.packaging && (
                                <InfoItem
                                  label={"Packaging"}
                                  value={offering.packaging}
                                />
                              )}
                              <InfoItem
                                label={"Minimum order quantity"}
                                value={offering.minimum_order_quantity.toLocaleString()}
                              />
                            </CellRow>
                            <CellRow>
                              {offering.price_breaks.map((priceBreak) => {
                                return (
                                  <InfoItem
                                    key={priceBreak.quantity}
                                    label={priceBreak.quantity.toLocaleString()}
                                    value={formatSmallCurrency(
                                      priceBreak.unit_price,
                                      priceBreak.currency,
                                    )}
                                  />
                                );
                              })}
                            </CellRow>
                          </InventoryTableCell>
                        </TableRow>
                      );
                    },
                  );
                })}
                {_.orderBy(
                  inventoryResponse.physical_inventory.containers,
                  "inventory_level",
                  "desc",
                ).map((container) => {
                  return (
                    <TableRow key={container.flashpcb_container_barcode}>
                      <InventoryTableCell>Physical</InventoryTableCell>
                      <InventoryTableCell>
                        {container.container_type}
                      </InventoryTableCell>
                      <InventoryTableCell alignRight>
                        <div>
                          <InputLabel>Last counted amount</InputLabel>
                          <div>
                            {container.last_counted_amount.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <InputLabel>Available inventory</InputLabel>
                          <div>
                            {container.available_inventory_level.toLocaleString()}
                          </div>
                        </div>
                      </InventoryTableCell>
                      <InventoryTableCell>
                        <FakeLink onClick={() => onSelectContainer(container)}>
                          Edit
                        </FakeLink>
                        <FakeLink
                          onClick={() =>
                            setContainerToDelete(
                              container.flashpcb_container_barcode,
                            )
                          }
                        >
                          Delete container
                        </FakeLink>
                      </InventoryTableCell>
                      <InventoryTableCell>
                        {container.nickname && (
                          <InfoItem
                            label={"Nickname"}
                            value={container.nickname}
                          />
                        )}
                        <InfoItem
                          label={"FPCB Container Barcode"}
                          value={
                            <ContainerLink
                              containerBarcode={
                                container.flashpcb_container_barcode
                              }
                            />
                          }
                        />{" "}
                        <InfoItem
                          label={"FPCB Bin Barcode"}
                          value={
                            <BinLink
                              binBarcode={container.flashpcb_bin_barcode}
                            />
                          }
                        />
                        <InfoItem
                          label={"Bin Name"}
                          value={container.bin_name}
                        />
                        <InfoItem
                          label={"Bin Location"}
                          value={container.bin_location}
                        />
                        {container.most_recent_update && (
                          <InfoItem
                            label={"Last update"}
                            value={datetimeStringToHumanReadableDatetime(
                              container.most_recent_update,
                            )}
                          />
                        )}
                        {container.consignment_container_owner_id && (
                          <ConsignmentUserInfo
                            userId={container.consignment_container_owner_id}
                          />
                        )}
                        {(container.part_allocations.length > 0 ||
                          container.spent_part_allocations.length > 0) && (
                          <AllocationsDetails
                            allocations={container.part_allocations}
                            spentAllocations={container.spent_part_allocations}
                          />
                        )}
                      </InventoryTableCell>
                    </TableRow>
                  );
                })}
                {inventoryResponse.virtual_inventory
                  .unapproved_sellers_inventory && (
                  <PartDrawerInventorySubTitle>
                    Additional virtual offers available for approval
                  </PartDrawerInventorySubTitle>
                )}
                {_.orderBy(
                  inventoryResponse.virtual_inventory
                    .unapproved_sellers_inventory,
                  "inventory_level",
                  "desc",
                ).flatMap((sellerInventory) => {
                  const numOfferings = sellerInventory.offerings.length;
                  return sellerInventory.offerings.map(
                    (offering, offeringIndex) => {
                      return (
                        <TableRow key={offering.distributor_link}>
                          {offeringIndex == 0 && (
                            <>
                              <InventoryTableCell rowSpan={numOfferings}>
                                Virtual
                              </InventoryTableCell>
                              <InventoryTableCell rowSpan={numOfferings}>
                                <>{sellerInventory.seller}</>
                                <>
                                  <ApproveVirtualInventory
                                    partId={partId}
                                    sellerInventory={sellerInventory}
                                    onFinishedEditing={onFinishedEditing}
                                  />
                                </>
                              </InventoryTableCell>
                              <InventoryTableCell
                                rowSpan={numOfferings}
                                alignRight
                              >
                                {sellerInventory.inventory_level.toLocaleString()}
                              </InventoryTableCell>
                            </>
                          )}
                          <InventoryTableCell>
                            <Link
                              href={offering.distributor_link}
                              target={"_blank"}
                            >
                              See offer
                            </Link>
                          </InventoryTableCell>
                          <InventoryTableCell>
                            <CellRow>
                              {offering.packaging && (
                                <InfoItem
                                  label={"Packaging"}
                                  value={offering.packaging}
                                />
                              )}
                              <InfoItem
                                label={"Minimum order quantity"}
                                value={offering.minimum_order_quantity.toLocaleString()}
                              />
                            </CellRow>
                            <CellRow>
                              {offering.price_breaks.map((priceBreak) => {
                                return (
                                  <InfoItem
                                    key={priceBreak.quantity}
                                    label={priceBreak.quantity.toLocaleString()}
                                    value={formatSmallCurrency(
                                      priceBreak.unit_price,
                                      priceBreak.currency,
                                    )}
                                  />
                                );
                              })}
                            </CellRow>
                          </InventoryTableCell>
                        </TableRow>
                      );
                    },
                  );
                })}
              </tbody>
            </NoWrapFlashPCBTable>
          </HorizontalScroll>
        </TableContainer>
        {containerToDelete !== null && (
          <Modal isOpen={true} onRequestClose={closeModal}>
            <ModalContentWithCloseButton closeModal={closeModal}>
              <DeleteContainer
                containerBarcode={containerToDelete}
                onFinishedEditing={closeModal}
              />
            </ModalContentWithCloseButton>
          </Modal>
        )}
      </>
    );
  }
}
